/**
 * =============================================================================
 * ANALYTICS DASHBOARD COMPONENT
 * =============================================================================
 *
 * Displays comprehensive analytics for the admin dashboard including:
 * - 7-day question trend (sparkline)
 * - Average response time
 * - Status distribution
 * - Recent activity feed
 *
 * PHASE_0C_SPEC.md: Task 0C-36
 *
 * =============================================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Box, Panel, Text, Flex, FlexItem, HR, Small } from '@bigcommerce/big-design';
import type { AnalyticsResponse, DailyQuestionCount, RecentActivityItem } from '@find-my-part/types';
import { adminApi } from '../../api/client';
import { useSession } from '../../hooks/useSession';
import { formatRelativeTime } from '../../utils/formatters';
import { QUESTION_STATUS_LABELS } from '@find-my-part/types';
import { tokens } from '../../tokens';
import { StatusChip } from '../common/StatusChip';

// =============================================================================
// TYPES
// =============================================================================

interface AnalyticsDashboardProps {
  /** Optional callback when data loads successfully */
  onDataLoaded?: (data: AnalyticsResponse) => void;
}

// Status color mapping for distribution bars
const STATUS_COLORS: Record<string, string> = tokens.color.chart.status;

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

/**
 * CSS-only sparkline chart for 7-day trend.
 * Note: Dates from API are in UTC (YYYY-MM-DD format).
 */
function SparklineChart({ data }: { data: DailyQuestionCount[] }): React.ReactElement {
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', height: '60px', gap: tokens.dimension.space.xs }}>
      {data.map((day, index) => {
        const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
        // Parse date as UTC to match backend timezone (IMP-6 fix)
        const dayLabel = new Date(`${day.date}T00:00:00Z`).toLocaleDateString('en-US', {
          weekday: 'short',
          timeZone: 'UTC'
        });

        return (
          <div
            key={day.date}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <div
              style={{
                width: '100%',
                height: `${Math.max(height, 4)}%`,
                backgroundColor: index === data.length - 1 ? tokens.color.brand.primary : tokens.color.chart.bar.track,
                borderRadius: tokens.dimension.radius.xs,
                minHeight: '4px',
                transition: 'height 0.3s ease',
              }}
              title={`${dayLabel}: ${day.count} question${day.count !== 1 ? 's' : ''}`}
            />
            <Small color="secondary">
              {dayLabel}
            </Small>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Status distribution bar chart.
 */
function StatusDistribution({
  distribution,
}: {
  distribution: AnalyticsResponse['statusDistribution'];
}): React.ReactElement {
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);

  // Display all 7 statuses in canonical order (per review feedback - IMP-5)
  // Sorted by count descending, but always show all statuses for full workflow visibility
  const entries = Object.entries(distribution)
    .sort((a, b) => b[1] - a[1]);

  if (total === 0) {
    return (
      <Text color="secondary" marginVertical="medium">
        No questions yet
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.dimension.space.sm }}>
      {entries.map(([status, count]) => {
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const label = QUESTION_STATUS_LABELS[status as keyof typeof QUESTION_STATUS_LABELS] || status;

        return (
          <div key={status}>
            <Flex justifyContent="space-between" marginBottom="xxSmall">
              <Small>{label}</Small>
              <Small color="secondary">
                {count} ({percentage.toFixed(0)}%)
              </Small>
            </Flex>
            <div
              style={{
                height: '8px',
                backgroundColor: tokens.color.chart.bar.track,
                borderRadius: tokens.dimension.radius.sm,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.max(percentage, count > 0 ? 2 : 0)}%`,
                  backgroundColor: STATUS_COLORS[status] || tokens.color.brand.primary,
                  borderRadius: tokens.dimension.radius.sm,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Recent activity feed item.
 */
function ActivityItem({ item }: { item: RecentActivityItem }): React.ReactElement {
  const actionIcons: Record<string, string> = {
    created: '+',
    updated: '~',
    resolved: '!',
  };

  const actionColors: Record<string, 'success' | 'warning' | 'secondary'> = {
    created: 'success',
    updated: 'secondary',
    resolved: 'success',
  };

  return (
    <Flex alignItems="flex-start" marginVertical="xSmall">
      <FlexItem flexShrink={0} marginRight="small">
        <StatusChip label={actionIcons[item.action] || '?'} variant={actionColors[item.action] || 'secondary'} />
      </FlexItem>
      <FlexItem flexGrow={1}>
        <Text marginBottom="none">
          {item.title.length > 50 ? `${item.title.slice(0, 50)}...` : item.title}
        </Text>
        <Small color="secondary">
          {item.action === 'created' ? 'New question' : item.action === 'resolved' ? 'Resolved' : 'Updated'}
          {' '}&middot;{' '}
          {formatRelativeTime(item.timestamp)}
        </Small>
      </FlexItem>
    </Flex>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Analytics Dashboard Component.
 *
 * Displays key metrics and trends for store owners to understand
 * their Q&A activity at a glance.
 */
export function AnalyticsDashboard({ onDataLoaded }: AnalyticsDashboardProps): React.ReactElement {
  const { token } = useSession();
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async (): Promise<void> => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await adminApi.analytics.get(token);

      if (response.success) {
        setAnalytics(response.data);
        onDataLoaded?.(response.data);
      } else {
        setError(response.error.message || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, onDataLoaded]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Loading state
  if (loading) {
    return (
      <Panel header="Analytics">
        <Box padding="medium">
          <Text color="secondary">Loading analytics...</Text>
        </Box>
      </Panel>
    );
  }

  // Error state
  if (error) {
    return (
      <Panel header="Analytics">
        <Box padding="medium">
          <Text color="danger">{error}</Text>
        </Box>
      </Panel>
    );
  }

  // No data state
  if (!analytics) {
    return (
      <Panel header="Analytics">
        <Box padding="medium">
          <Text color="secondary">No analytics data available</Text>
        </Box>
      </Panel>
    );
  }

  return (
    <Panel header="Analytics Dashboard" data-testid="analytics-dashboard">
      <Box padding="medium">
        {/* Top Row: Key Metrics */}
        <Flex flexDirection={{ mobile: 'column', tablet: 'row' }} flexGap="1rem" marginBottom="large">
          {/* Questions This Week */}
          <FlexItem flexGrow={1}>
            <Box
              padding="medium"
              borderRadius="normal"
              style={{ textAlign: 'center', border: `1px solid ${tokens.color.border.default}`, background: tokens.color.surface.subtle }}
            >
              <Text bold marginBottom="none">
                {analytics.weeklyTotal}
              </Text>
              <Small color="secondary">Questions This Week</Small>
            </Box>
          </FlexItem>

          {/* Average Response Time */}
          <FlexItem flexGrow={1}>
            <Box
              padding="medium"
              borderRadius="normal"
              style={{ textAlign: 'center', border: `1px solid ${tokens.color.border.default}`, background: tokens.color.surface.subtle }}
            >
              <Text bold marginBottom="none">
                {analytics.averageResponseTimeHours !== null
                  ? `${analytics.averageResponseTimeHours}h`
                  : 'N/A'}
              </Text>
              <Small color="secondary">Avg. Response Time</Small>
            </Box>
          </FlexItem>

          {/* Resolution Rate */}
          <FlexItem flexGrow={1}>
            <Box
              padding="medium"
              borderRadius="normal"
              style={{ textAlign: 'center', border: `1px solid ${tokens.color.border.default}`, background: tokens.color.surface.subtle }}
            >
              {(() => {
                const total = Object.values(analytics.statusDistribution).reduce((a, b) => a + b, 0);
                const resolved = analytics.statusDistribution.RESOLVED;
                const rate = total > 0 ? Math.round((resolved / total) * 100) : 0;
                return (
                  <>
                    <Text bold marginBottom="none">
                      {rate}%
                    </Text>
                    <Small color="secondary">Resolution Rate</Small>
                  </>
                );
              })()}
            </Box>
          </FlexItem>

          {/* Total Questions */}
          <FlexItem flexGrow={1}>
            <Box
              padding="medium"
              borderRadius="normal"
              style={{ textAlign: 'center', border: `1px solid ${tokens.color.border.default}`, background: tokens.color.surface.subtle }}
            >
              <Text bold marginBottom="none">
                {Object.values(analytics.statusDistribution).reduce((a, b) => a + b, 0)}
              </Text>
              <Small color="secondary">Total Questions</Small>
            </Box>
          </FlexItem>
        </Flex>

        <HR marginVertical="medium" />

        {/* Middle Row: Charts */}
        <Flex flexDirection={{ mobile: 'column', tablet: 'row' }} flexGap="1.5rem" marginBottom="large">
          {/* 7-Day Trend */}
          <FlexItem flexGrow={1} flexBasis="50%">
            <Text bold marginBottom="small">
              Questions (Last 7 Days)
            </Text>
            <SparklineChart data={analytics.questionsThisWeek} />
          </FlexItem>

          {/* Status Distribution */}
          <FlexItem flexGrow={1} flexBasis="50%">
            <Text bold marginBottom="small">
              Status Distribution
            </Text>
            <StatusDistribution distribution={analytics.statusDistribution} />
          </FlexItem>
        </Flex>

        <HR marginVertical="medium" />

        {/* Bottom Row: Recent Activity */}
        <Text bold marginBottom="small">
          Recent Activity
        </Text>
        {analytics.recentActivity.length > 0 ? (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {analytics.recentActivity.map((item) => (
              <ActivityItem key={`${item.questionId}-${item.timestamp}`} item={item} />
            ))}
          </div>
        ) : (
          <Text color="secondary">No recent activity</Text>
        )}
      </Box>
    </Panel>
  );
}

export default AnalyticsDashboard;
